import { clampToRoom, defaultSize, normalizeRotation, rotateBy, snapPoint } from "./geometry";
import {
  normalizePair,
  pairKey,
  planFromResponse,
  type DraftPlan,
  type DraftTable,
  type DraftZone,
  type EditorState,
  type ShapeKind,
  type Tool,
  type ZoneKind,
} from "./types";
import type { FloorPlanResponse } from "../floorApi";

/**
 * The buffered floor-plan draft reducer (Implementation plan §2). Actions are ELEMENT-granular
 * (`moveTable`, `setCovers`, `togglePair`, `setZoneCapacity`, `retireTable`, …) so each is a single
 * undoable step; the whole draft is flushed as one CAS PUT (FL-6). Undo/redo is bounded at 50 entries
 * (Tech-decision "Save model"). Every field here is serializable and pure — no React, no DOM — so the
 * reducer is exhaustively table-testable, which is the acceptance-criteria core.
 */

export const UNDO_LIMIT = 50;

export type EditorAction =
  /** Adopt a freshly loaded / reloaded server plan as the new clean base (clears dirty + history). */
  | { type: "INIT"; plan: DraftPlan }
  /** Keep the current draft edits but rebase them onto the server's current version (409 "re-apply"). */
  | { type: "REBASE"; version: number }
  /** Reset to a clean, saved state at the returned version (post-save success). */
  | { type: "SAVED"; version: number }
  | { type: "SELECT_TABLE"; id: string }
  | { type: "SELECT_ZONE"; id: string }
  | { type: "CLEAR_SELECTION" }
  | { type: "SET_TOOL"; tool: Tool }
  | { type: "SET_ACTIVE_ZONE"; zoneId: string }
  | { type: "ADD_ZONE"; zone: DraftZone }
  | { type: "RENAME_ZONE"; id: string; name: string }
  | { type: "SET_ZONE_KIND"; id: string; kind: ZoneKind }
  | { type: "SET_ZONE_CAPACITY"; id: string; capacity: number | null; legalCapacity: number | null }
  | { type: "MOVE_ZONE"; id: string; dir: -1 | 1 }
  | { type: "REMOVE_ZONE"; id: string }
  | { type: "ADD_TABLE"; zoneId: string; table?: Partial<DraftTable> }
  | { type: "MOVE_TABLE"; id: string; x: number; y: number }
  | { type: "NUDGE_TABLE"; id: string; dx: number; dy: number }
  | { type: "ROTATE_TABLE"; id: string; delta: number }
  | { type: "SET_TABLE_ROTATION"; id: string; deg: number }
  | { type: "SET_TABLE_SHAPE"; id: string; shape: ShapeKind }
  | { type: "SET_TABLE_LABEL"; id: string; label: string }
  | { type: "SET_TABLE_POSITION"; id: string; x: number; y: number }
  | { type: "SET_COVERS"; id: string; minCovers: number; maxCovers: number }
  | { type: "SET_PRIORITY"; id: string; priorityOrder: number }
  | { type: "RETIRE_TABLE"; id: string }
  | { type: "RESTORE_TABLE"; id: string }
  | { type: "TOGGLE_PAIR"; a: string; b: string }
  | { type: "LINK_SELECT"; id: string }
  | { type: "SET_POLICY"; oversizeCap: number; spreadAcrossZones: boolean }
  | { type: "UNDO" }
  | { type: "REDO" };

/** A stable v4 UUID for a new element — a valid `TableId`/`ZoneId` the backend accepts verbatim. */
export function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Deterministic-enough fallback for environments without WebCrypto (never the browser/jsdom).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function initialState(plan: DraftPlan): EditorState {
  return {
    plan,
    baseVersion: plan.version,
    selection: { kind: "none" },
    tool: "SELECT",
    activeZoneId: plan.zones[0]?.id ?? null,
    linkAnchor: null,
    dirty: false,
    past: [],
    future: [],
  };
}

export function initialStateFromResponse(res: FloorPlanResponse): EditorState {
  return initialState(planFromResponse(res));
}

/** Push the current plan onto the bounded undo stack and clear redo — called before any mutation. */
function checkpoint(state: EditorState): Pick<EditorState, "past" | "future"> {
  const past = [...state.past, state.plan];
  if (past.length > UNDO_LIMIT) past.shift();
  return { past, future: [] };
}

/** Apply an element-level mutation: snapshot, replace the plan, mark dirty. */
function mutate(state: EditorState, plan: DraftPlan): EditorState {
  return { ...state, ...checkpoint(state), plan, dirty: true };
}

function mapTable(plan: DraftPlan, id: string, fn: (t: DraftTable) => DraftTable): DraftPlan {
  return { ...plan, tables: plan.tables.map((t) => (t.id === id ? fn(t) : t)) };
}

function mapZone(plan: DraftPlan, id: string, fn: (z: DraftZone) => DraftZone): DraftPlan {
  return { ...plan, zones: plan.zones.map((z) => (z.id === id ? fn(z) : z)) };
}

export function reducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "INIT":
      return initialState(action.plan);

    case "REBASE":
      // Keep every local edit; only the CAS token moves to the server's current version so the next
      // Save re-applies the draft on top of their record (never an auto-merge of geometry).
      return {
        ...state,
        plan: { ...state.plan, version: action.version },
        baseVersion: action.version,
        dirty: true,
      };

    case "SAVED":
      return {
        ...state,
        plan: { ...state.plan, version: action.version },
        baseVersion: action.version,
        dirty: false,
        past: [],
        future: [],
      };

    case "SELECT_TABLE":
      return { ...state, selection: { kind: "table", id: action.id } };

    case "SELECT_ZONE":
      return { ...state, selection: { kind: "zone", id: action.id }, activeZoneId: action.id };

    case "CLEAR_SELECTION":
      return { ...state, selection: { kind: "none" } };

    case "SET_TOOL":
      return { ...state, tool: action.tool, linkAnchor: null };

    case "SET_ACTIVE_ZONE":
      return { ...state, activeZoneId: action.zoneId };

    case "ADD_ZONE": {
      const next = mutate(state, { ...state.plan, zones: [...state.plan.zones, action.zone] });
      return {
        ...next,
        activeZoneId: action.zone.id,
        selection: { kind: "zone", id: action.zone.id },
      };
    }

    case "RENAME_ZONE":
      return mutate(
        state,
        mapZone(state.plan, action.id, (z) => ({ ...z, name: action.name })),
      );

    case "SET_ZONE_KIND":
      return mutate(
        state,
        mapZone(state.plan, action.id, (z) => ({
          ...z,
          kind: action.kind,
          capacity: action.kind === "STANDING_HEADCOUNT" ? (z.capacity ?? 0) : null,
          legalCapacity: action.kind === "STANDING_HEADCOUNT" ? z.legalCapacity : null,
        })),
      );

    case "SET_ZONE_CAPACITY":
      return mutate(
        state,
        mapZone(state.plan, action.id, (z) => ({
          ...z,
          capacity: action.capacity,
          legalCapacity: action.legalCapacity,
        })),
      );

    case "MOVE_ZONE": {
      const zones = [...state.plan.zones];
      const i = zones.findIndex((z) => z.id === action.id);
      const j = i + action.dir;
      if (i < 0 || j < 0 || j >= zones.length) return state;
      [zones[i], zones[j]] = [zones[j]!, zones[i]!];
      return mutate(state, { ...state.plan, zones });
    }

    case "REMOVE_ZONE": {
      // New (never-saved) tables in the zone are dropped; persisted ones are soft-retired so their
      // ids survive (bookings may still resolve them) — the zone itself is removed from the layout.
      const tables = state.plan.tables
        .filter((t) => !(t.zoneId === action.id && t.isNew))
        .map((t) => (t.zoneId === action.id ? { ...t, retired: true } : t));
      const zones = state.plan.zones.filter((z) => z.id !== action.id);
      const next = mutate(state, { ...state.plan, zones, tables });
      return { ...next, activeZoneId: zones[0]?.id ?? null, selection: { kind: "none" } };
    }

    case "ADD_TABLE": {
      const id = action.table?.id ?? newId();
      const shape: ShapeKind = action.table?.shape ?? "ROUND";
      const size = defaultSize(shape);
      const count = state.plan.tables.filter((t) => t.zoneId === action.zoneId).length;
      const pos = snapPoint(
        action.table?.x ?? 60 + (count % 6) * (size.w + 20),
        action.table?.y ?? 60 + Math.floor(count / 6) * (size.h + 20),
      );
      const table: DraftTable = {
        id,
        zoneId: action.zoneId,
        label: action.table?.label ?? `${count + 1}`,
        shape,
        x: pos.x,
        y: pos.y,
        rotationDeg: action.table?.rotationDeg ?? 0,
        minCovers: action.table?.minCovers ?? 2,
        maxCovers: action.table?.maxCovers ?? 4,
        priorityOrder: action.table?.priorityOrder ?? count,
        retired: false,
        isNew: true,
      };
      const next = mutate(state, { ...state.plan, tables: [...state.plan.tables, table] });
      return { ...next, selection: { kind: "table", id } };
    }

    case "MOVE_TABLE":
    case "SET_TABLE_POSITION": {
      const { x, y } = clampToRoom(action.x, action.y);
      const snapped = action.type === "MOVE_TABLE" ? snapPoint(x, y) : { x, y };
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({ ...t, x: snapped.x, y: snapped.y })),
      );
    }

    case "NUDGE_TABLE":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => {
          const { x, y } = clampToRoom(t.x + action.dx, t.y + action.dy);
          return { ...t, x, y };
        }),
      );

    case "ROTATE_TABLE":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({
          ...t,
          rotationDeg: rotateBy(t.rotationDeg, action.delta),
        })),
      );

    case "SET_TABLE_ROTATION":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({
          ...t,
          rotationDeg: normalizeRotation(action.deg),
        })),
      );

    case "SET_TABLE_SHAPE":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({ ...t, shape: action.shape })),
      );

    case "SET_TABLE_LABEL":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({ ...t, label: action.label })),
      );

    case "SET_COVERS":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({
          ...t,
          minCovers: action.minCovers,
          maxCovers: action.maxCovers,
        })),
      );

    case "SET_PRIORITY":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({ ...t, priorityOrder: action.priorityOrder })),
      );

    case "RETIRE_TABLE": {
      const table = state.plan.tables.find((t) => t.id === action.id);
      if (!table) return state;
      if (table.isNew) {
        // Delete-before-first-save: drop it entirely AND any pair that referenced it.
        const tables = state.plan.tables.filter((t) => t.id !== action.id);
        const combinables = state.plan.combinables.filter(
          (p) => p.a !== action.id && p.b !== action.id,
        );
        const next = mutate(state, { ...state.plan, tables, combinables });
        return { ...next, selection: { kind: "none" } };
      }
      // Soft retire (§C.5): keep the id + drop it from the combine list (a retired table never combines).
      const combinables = state.plan.combinables.filter(
        (p) => p.a !== action.id && p.b !== action.id,
      );
      return mutate(state, {
        ...mapTable(state.plan, action.id, (t) => ({ ...t, retired: true })),
        combinables,
      });
    }

    case "RESTORE_TABLE":
      return mutate(
        state,
        mapTable(state.plan, action.id, (t) => ({ ...t, retired: false })),
      );

    case "TOGGLE_PAIR": {
      if (action.a === action.b) return state; // a table never combines with itself (§C.5)
      const pair = normalizePair(action.a, action.b);
      const key = pairKey(pair);
      const exists = state.plan.combinables.some((p) => pairKey(p) === key);
      const combinables = exists
        ? state.plan.combinables.filter((p) => pairKey(p) !== key)
        : [...state.plan.combinables, pair];
      return mutate(state, { ...state.plan, combinables });
    }

    case "LINK_SELECT": {
      // Link mode: first click sets the anchor, the second toggles the unordered pair (FL-2 — an
      // explicit, deliberate act; never inferred from drawn adjacency).
      if (state.linkAnchor === null) {
        return { ...state, linkAnchor: action.id, selection: { kind: "table", id: action.id } };
      }
      if (state.linkAnchor === action.id) return { ...state, linkAnchor: null };
      const toggled = reducer(state, { type: "TOGGLE_PAIR", a: state.linkAnchor, b: action.id });
      return { ...toggled, linkAnchor: null, selection: { kind: "table", id: action.id } };
    }

    case "SET_POLICY":
      return mutate(state, {
        ...state.plan,
        policy: { oversizeCap: action.oversizeCap, spreadAcrossZones: action.spreadAcrossZones },
      });

    case "UNDO": {
      if (state.past.length === 0) return state;
      const past = [...state.past];
      const previous = past.pop()!;
      return {
        ...state,
        plan: previous,
        past,
        future: [state.plan, ...state.future],
        dirty: true,
      };
    }

    case "REDO": {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        ...state,
        plan: next!,
        past: [...state.past, state.plan],
        future: rest,
        dirty: true,
      };
    }

    default:
      return state;
  }
}

export function canUndo(state: EditorState): boolean {
  return state.past.length > 0;
}

export function canRedo(state: EditorState): boolean {
  return state.future.length > 0;
}

export function findTable(plan: DraftPlan, id: string | undefined): DraftTable | undefined {
  return id ? plan.tables.find((t) => t.id === id) : undefined;
}

export function findZone(plan: DraftPlan, id: string | null | undefined): DraftZone | undefined {
  return id ? plan.zones.find((z) => z.id === id) : undefined;
}

export function tablesInZone(plan: DraftPlan, zoneId: string): DraftTable[] {
  return plan.tables.filter((t) => t.zoneId === zoneId);
}
